// Piano chord note mappings.
// Each chord = list of note names (root at index 0, then chord tones).
// Notes use sharps as canonical form. The renderer maps flats to sharps too.
const NOTE_TO_INDEX = {
  'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'Fb': 4,
  'F': 5, 'E#': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9,
  'A#': 10, 'Bb': 10, 'B': 11, 'Cb': 11,
};

// Chord quality → intervals (in semitones from root)
const QUALITY_INTERVALS = {
  '':      [0, 4, 7],           // major
  'maj':   [0, 4, 7],
  'M':     [0, 4, 7],
  'm':     [0, 3, 7],           // minor
  'min':   [0, 3, 7],
  '7':     [0, 4, 7, 10],
  'maj7':  [0, 4, 7, 11],
  'M7':    [0, 4, 7, 11],
  'm7':    [0, 3, 7, 10],
  'min7':  [0, 3, 7, 10],
  'dim':   [0, 3, 6],
  'dim7':  [0, 3, 6, 9],
  'm7b5':  [0, 3, 6, 10],
  'aug':   [0, 4, 8],
  '6':     [0, 4, 7, 9],
  'm6':    [0, 3, 7, 9],
  '9':     [0, 4, 7, 10, 14],
  'maj9':  [0, 4, 7, 11, 14],
  'm9':    [0, 3, 7, 10, 14],
  'sus2':  [0, 2, 7],
  'sus4':  [0, 5, 7],
  '7sus4': [0, 5, 7, 10],
  'add9':  [0, 4, 7, 14],
};

// Parse a chord name into { rootIndex, quality, bassIndex? }.
export function parseChord(name) {
  if (!name) return null;
  const clean = name.trim();
  // Slash chord? (e.g. C/G, F/A)
  const slashIdx = clean.indexOf('/');
  let base = clean, bass = null;
  if (slashIdx > 0) {
    base = clean.slice(0, slashIdx);
    bass = clean.slice(slashIdx + 1);
  }
  // Extract root (1 or 2 chars: letter + optional # or b)
  const m = base.match(/^([A-G])(#|b)?(.*)$/);
  if (!m) return null;
  const rootName = m[1] + (m[2] || '');
  const rest = m[3] || '';
  if (!(rootName in NOTE_TO_INDEX)) return null;
  const rootIndex = NOTE_TO_INDEX[rootName];
  // Determine quality
  let quality = rest;
  if (!(quality in QUALITY_INTERVALS)) {
    // Simplify: strip after common qualifiers
    // fallback to major
    quality = quality in QUALITY_INTERVALS ? quality : '';
  }
  let bassIndex = null;
  if (bass) {
    const bm = bass.match(/^([A-G])(#|b)?/);
    if (bm) {
      const bn = bm[1] + (bm[2] || '');
      if (bn in NOTE_TO_INDEX) bassIndex = NOTE_TO_INDEX[bn];
    }
  }
  return { rootIndex, quality, bassIndex };
}

// Return absolute note indices (0-24 across 2 octaves) starting at octave 4.
export function getChordNoteIndices(name) {
  const parsed = parseChord(name);
  if (!parsed) return null;
  const { rootIndex, quality, bassIndex } = parsed;
  const intervals = QUALITY_INTERVALS[quality] || QUALITY_INTERVALS[''];
  // Anchor: root at note index (0 = C4). Start of piano rendering.
  // Push tones as absolute semitones from C4.
  const notes = intervals.map(iv => rootIndex + iv);
  if (bassIndex !== null && bassIndex !== undefined) {
    // Bass note goes an octave below (subtract 12 if bass > root)
    const bassAbs = bassIndex < rootIndex ? bassIndex : bassIndex - 12;
    notes.unshift(bassAbs);
  }
  // Return sorted, unique
  return Array.from(new Set(notes)).sort((a, b) => a - b);
}
